import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Search, Gift, Loader2, Users, BadgeCheck, Building2, Send } from "lucide-react";

const PLANS = ["Gratuito", "Plus", "Médico Profissional", "Premium"];
const PLAN_CLS = {
  Gratuito: "bg-secondary text-muted-foreground border-border",
  Plus: "bg-chart-3/12 text-chart-3 border-chart-3/25",
  "Médico Profissional": "bg-ai/12 text-ai border-ai/25",
  Premium: "bg-success/12 text-success border-success/25",
};

export default function AdminPanel() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("todos");
  const [selected, setSelected] = useState(null);

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (role !== "todos") params.set("role", role);
    api.get(`/admin/accounts?${params.toString()}`).then((r) => setAccounts(r.data)).catch(() => {});
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [role]); // eslint-disable-line

  if (user?.role !== "admin") {
    return <div className="p-10 text-muted-foreground" data-testid="admin-denied">Acesso restrito ao administrador.</div>;
  }

  const courtesy = async (id) => {
    try { await api.post(`/admin/accounts/${id}/courtesy`); toast.success("Cortesia Premium de 30 dias concedida"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 sm:p-10 max-w-7xl" data-testid="admin-panel">
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-ai mb-2 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Administração</p>
        <h1 className="font-heading text-4xl font-light tracking-tighter">Cadastros da plataforma</h1>
        <p className="text-muted-foreground text-sm mt-1">Gerir contas ativas, assinaturas e cortesias.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="admin-stats">
        <Card className="p-4 border-border shadow-none"><div className="flex items-center gap-2 text-muted-foreground text-xs"><Users className="w-4 h-4" /> Total</div><div className="font-mono-data text-2xl font-semibold mt-1">{stats ? stats.total : "—"}</div></Card>
        <Card className="p-4 border-border shadow-none"><div className="flex items-center gap-2 text-muted-foreground text-xs"><BadgeCheck className="w-4 h-4 text-success" /> Ativos</div><div className="font-mono-data text-2xl font-semibold mt-1">{stats ? stats.active : "—"}</div></Card>
        <Card className="p-4 border-border shadow-none"><div className="flex items-center gap-2 text-muted-foreground text-xs"><Users className="w-4 h-4" /> Médicos</div><div className="font-mono-data text-2xl font-semibold mt-1">{stats ? stats.medicos : "—"}</div></Card>
        <Card className="p-4 border-border shadow-none"><div className="flex items-center gap-2 text-muted-foreground text-xs"><Building2 className="w-4 h-4" /> Laboratórios</div><div className="font-mono-data text-2xl font-semibold mt-1">{stats ? stats.laboratorios : "—"}</div></Card>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Buscar por nome ou e-mail…" className="pl-9" data-testid="admin-search" />
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-48" data-testid="admin-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os tipos</SelectItem>
            <SelectItem value="medico">Médicos</SelectItem>
            <SelectItem value="laboratorio">Laboratórios</SelectItem>
            <SelectItem value="admin">Administradores</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} data-testid="admin-search-btn"><Search className="w-4 h-4 mr-2" /> Buscar</Button>
      </div>

      <Card className="p-0 border-border shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Plano</TableHead>
                <TableHead className="text-center">Pacientes</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.map((a) => (
                <TableRow key={a.id} data-testid={`admin-row-${a.id}`}>
                  <TableCell>
                    <p className="font-medium">{a.name}</p>
                    <p className="text-xs text-muted-foreground">{a.email}</p>
                  </TableCell>
                  <TableCell className="text-sm capitalize text-muted-foreground">{a.role}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={PLAN_CLS[a.plan] || ""}>{a.plan}</Badge>
                    {a.plan_expires_at && <p className="text-xs text-muted-foreground mt-1">até {a.plan_expires_at.slice(0, 10)} · {a.plan_status}</p>}
                  </TableCell>
                  <TableCell className="text-center font-mono-data">{a.patients}</TableCell>
                  <TableCell className="text-center">
                    {a.active ? <Badge variant="outline" className="bg-success/12 text-success border-success/25">Ativo</Badge>
                      : <Badge variant="outline" className="bg-destructive/12 text-destructive border-destructive/25">Inativo</Badge>}
                    {a.pending_requests > 0 && <p className="text-[10px] text-warning mt-1">{a.pending_requests} pendência(s)</p>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => courtesy(a.id)} data-testid={`admin-courtesy-${a.id}`}>
                        <Gift className="w-4 h-4 mr-1" /> Cortesia
                      </Button>
                      <Button size="sm" onClick={() => setSelected(a.id)} data-testid={`admin-manage-${a.id}`}>Gerir</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {accounts.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Nenhum cadastro encontrado.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {selected && <ManageDialog id={selected} onClose={() => setSelected(null)} onChanged={load} />}
    </div>
  );
}

function ManageDialog({ id, onClose, onChanged }) {
  const [acc, setAcc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [plan, setPlan] = useState("Gratuito");
  const [months, setMonths] = useState("1");
  const [msg, setMsg] = useState("");

  const load = () => api.get(`/admin/accounts/${id}`).then((r) => { setAcc(r.data); setPlan(r.data.plan || "Gratuito"); });
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const toggleActive = async (v) => {
    await api.put(`/admin/accounts/${id}`, { active: v });
    toast.success(v ? "Cadastro ativado" : "Cadastro desativado");
    load(); onChanged();
  };

  const saveSub = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/accounts/${id}/subscription`, { plan, months: parseInt(months) || 1 });
      toast.success("Assinatura atualizada");
      load(); onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sendInfo = async () => {
    if (!msg.trim()) { toast.error("Escreva a mensagem"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/accounts/${id}/request-info`, { message: msg });
      toast.success("Solicitação enviada ao profissional");
      setMsg(""); load(); onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="admin-manage-dialog">
        <DialogHeader>
          <DialogTitle>{acc?.name || "Cadastro"}</DialogTitle>
          <DialogDescription>{acc?.email} · {acc?.role}</DialogDescription>
        </DialogHeader>
        {!acc ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-8" /> : (
          <div className="space-y-6 py-2">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-muted-foreground">Pacientes:</span> {acc.patients}</div>
              <div><span className="text-muted-foreground">Desde:</span> {(acc.created_at || "").slice(0, 10)}</div>
              <div><span className="text-muted-foreground">Plano:</span> {acc.plan} {acc.plan_expires_at ? `(até ${acc.plan_expires_at.slice(0, 10)})` : ""}</div>
              {acc.clinic_profile?.name && <div><span className="text-muted-foreground">Clínica:</span> {acc.clinic_profile.name}</div>}
            </div>

            <div className="flex items-center justify-between border border-border rounded-md p-3">
              <div><p className="text-sm font-medium">Cadastro ativo</p><p className="text-xs text-muted-foreground">Desativar bloqueia o acesso do profissional.</p></div>
              <Switch checked={acc.active} onCheckedChange={toggleActive} data-testid="admin-active-switch" />
            </div>

            <div className="border border-border rounded-md p-3 space-y-3">
              <p className="text-sm font-medium">Conceder assinatura / voucher</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Plano</Label>
                  <Select value={plan} onValueChange={setPlan}>
                    <SelectTrigger data-testid="admin-plan-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{PLANS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Meses</Label>
                  <Input type="number" min="1" value={months} onChange={(e) => setMonths(e.target.value)} data-testid="admin-months-input" /></div>
              </div>
              <Button size="sm" onClick={saveSub} disabled={saving} data-testid="admin-save-sub-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Aplicar assinatura
              </Button>
            </div>

            <div className="border border-border rounded-md p-3 space-y-3">
              <p className="text-sm font-medium">Solicitar informações</p>
              <Textarea rows={2} value={msg} onChange={(e) => setMsg(e.target.value)}
                placeholder="Ex: Envie o número do CRM e comprovante." data-testid="admin-info-msg" />
              <Button size="sm" variant="outline" onClick={sendInfo} disabled={saving} data-testid="admin-send-info-btn">
                <Send className="w-4 h-4 mr-2" /> Enviar solicitação
              </Button>
              {acc.info_requests?.filter((r) => !r.resolved).length > 0 && (
                <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                  <p className="font-medium mb-1">Pendências em aberto:</p>
                  {acc.info_requests.filter((r) => !r.resolved).map((r) => (
                    <p key={r.id}>• {r.message}</p>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="admin-close-btn">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
